import { ExtensionActionIdSchema, ExtensionFieldIdSchema, ExtensionIdSchema } from '../index.js';
// =============================================================================
// TYPE GUARDS
// =============================================================================
/**
 * Check if extension has actions (type guard)
 */
export function hasActions(extension) {
    if (!isGlobalExtension(extension)) {
        return false;
    }
    return extension.actions !== undefined && Array.isArray(extension.actions) && extension.actions.length > 0;
}
/**
 * Check if extension has fields (type guard)
 */
export function hasFields(extension) {
    if (!isUIExtension(extension)) {
        return false;
    }
    return extension.fields !== undefined && Array.isArray(extension.fields) && extension.fields.length > 0;
}
/**
 * Check if extension is a UI (below-shipping) extension (type guard).
 *
 * Positive check on the discriminator: a `fulfillment-constraint` (or any future)
 * context must NOT be treated as a UI extension — that would let callers reach for
 * `.fields` that do not exist.
 *
 * @example
 * if (isUIExtension(extension)) {
 *   // extension.fields is now accessible
 * }
 */
export function isUIExtension(extension) {
    return extension.context === 'below-shipping';
}
/**
 * Check if extension is a fulfillment-constraint extension (type guard).
 *
 * @example
 * if (isFulfillmentConstraintExtension(extension)) {
 *   for (const rule of extension.rules) {
 *     // rule.productIds / rule.collectionIds
 *   }
 * }
 */
export function isFulfillmentConstraintExtension(extension) {
    return extension.context === 'fulfillment-constraint';
}
/**
 * Check if extension is global (type guard)
 */
export function isGlobalExtension(extension) {
    return extension.context === 'global';
}
/**
 * Check if extension is below-shipping (type guard)
 */
export function isBelowShippingExtension(extension) {
    return extension.context === 'below-shipping';
}
/**
 * Check if field is checkbox (type guard)
 */
export function isCheckboxField(field) {
    return field.type === 'checkbox';
}
/**
 * Check if action is for order creation (type guard)
 */
export function isOrderCreationAction(action) {
    return action.trigger === 'on-order-creation';
}
/**
 * Check if action is for discount application (type guard)
 */
export function isDiscountApplicationAction(action) {
    return action.trigger === 'on-discount-application';
}
/**
 * Check if action is for checkout initiation (type guard)
 */
export function isCheckoutInitiationAction(action) {
    return action.trigger === 'on-checkout-initiation';
}
/**
 * Check if field has actions (type guard)
 */
export function fieldHasActions(field) {
    return field.actions !== undefined && Array.isArray(field.actions) && field.actions.length > 0;
}
/**
 * @description Check if action is of a specific type
 *
 *
 * @example
 * const action = { type: 'add-order-custom-attribute' };
 * if (isActionType(action, 'add-order-custom-attribute')) {
 *   console.log('Action is of type add-order-custom-attribute');
 * }
 *
 */
export function isActionType(action, type) {
    return action.type === type;
}
/**
 * Validate if a string is a valid extension ID
 */
export function isExtensionId(id) {
    return ExtensionIdSchema.safeParse(id).success;
}
/**
 * Validate if a string is a valid field ID
 */
export function isExtensionFieldId(id) {
    return ExtensionFieldIdSchema.safeParse(id).success;
}
/**
 * Validate if a string is a valid action ID
 */
export function isExtensionActionId(id) {
    return ExtensionActionIdSchema.safeParse(id).success;
}
export function isActiveExtension(extension) {
    return extension.enabled === true;
}
//# sourceMappingURL=type-guards.js.map